/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState, useRef, useLayoutEffect } from 'react';
import { Button } from 'reactstrap';
import { CurrentUserData } from '../../reducers/current-user';
import G17Client from '../../services/G17Client';
import { useAppDispatch } from '../../reducers';
import { addSiteAlert } from '../../slice/siteAlertsSlice';
import { MentionsInput, Mention, SuggestionDataItem } from 'react-mentions';
import { BlockingLoader } from '../loader/BlockingLoader';
import './styles.scss';
import { renderSuggestion } from './utils';

interface AddCommentProps {
  users: SuggestionDataItem[];
  toggle: () => void;
  utrvId: string;
  user: CurrentUserData;
  handleReload: () => void;
}

const AddComment = ({ users, toggle, utrvId, user, handleReload }: AddCommentProps) => {

  const dispatch = useAppDispatch();
  const [comment, setComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const textareaRef = useRef<null | HTMLTextAreaElement>(null);

  const handleSubmit = () => {
    setIsLoading(true);
    G17Client
      .addUtrvComment(utrvId, { text: comment })
      .then(() => {
        setComment('');
        handleReload();
        setIsLoading(false);
      })
      .catch((e: Error) => {
        dispatch(addSiteAlert({
          content: e.message ?? 'There was a problem submitting your comment. Please refresh and try again.'
        }));
        setIsLoading(false);
      })
  }

  const handleChange = ({ target }: { target: { value: string } }): void => setComment(target.value);

  useLayoutEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'inherit';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [comment]);

  const formValid = comment.length > 0;
  return (
    <>
      {isLoading ? <BlockingLoader /> : null}
      <div className='card mb-2'>
        <div className='card-body'>
          <div className='card-title strong'>{user.name}</div>
          <div className='mt-3 card-text'>
            <MentionsInput
              className='mentions-input mb-3'
              placeholder='Write a comment'
              inputRef={textareaRef}
              value={comment}
              onChange={handleChange}>
              <Mention
                trigger='@'
                data={users}
                className='mention'
                renderSuggestion={renderSuggestion}
                displayTransform={(id, display) => `@${display}`}
                appendSpaceOnAdd={true}
              />
            </MentionsInput>
          </div>
          <div className='mt-3 d-flex justify-content-between'>
            <Button color='link-secondary' onClick={toggle} className='text-ThemeHeadingLight'>Cancel</Button>
            <Button color='primary' disabled={!formValid} onClick={handleSubmit}>Comment</Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddComment;
